/* RAI Quote Form - Vanilla JavaScript Implementation */

class RAIQuoteForm {
  constructor() {
    this.currentStep = 0;
    this.totalSteps = 13; // we have 10 form steps (0-9) + loading (10) + success (11) + error (12)
    this.photos = {
      front: null,
      back: null,
      interior: null,
      bottom: null,
      serialNum: null,
      damage: null
    };
    this.init();
  }

  init() {
    this.bindEvents();
    // Ensure proper step initialization
    this.currentStep = 0;
    // Hide all slides first, then show step 0
    this.hideAllSlides();
    this.showStep(0);
    
    // Initialize progress bar to step 0 (10%)
    this.updateProgressBar(0);
  }

  bindEvents() {
    // Navigation buttons (including Get Started button)
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-action="next"]')) {
        this.nextStep();
      }
      if (e.target.matches('[data-action="back"]')) {
        this.previousStep();
      }
    });

    // Photo upload functionality
    this.initPhotoUploads();
    
    // Brand selection functionality
    this.initBrandSelection();
    
    // Category selection functionality
    this.initCategorySelection();
    
    // Model input functionality
    this.initModelInput();
    
    // Condition selection functionality
    this.initConditionSelection();
    
    // Details form functionality
    this.initDetailsForm();
    
    // Terms agreement functionality
    this.initTermsAgreement();
  }

  // Initialize brand selection
  initBrandSelection() {
    const brandSelect = document.getElementById('brand-select');
    if (brandSelect) {
      brandSelect.addEventListener('change', (e) => {
        const selectedBrand = e.target.value;
        this.updateBrandSelectionState(selectedBrand);
      });
    }
  }

  // Update brand selection state
  updateBrandSelectionState(selectedBrand) {
    const nextBtn = document.querySelector('[data-step="2"] .rai-next-btn');
    if (nextBtn) {
      nextBtn.disabled = !selectedBrand;
    }
  }

  // Initialize category selection
  initCategorySelection() {
    const categorySelect = document.getElementById('category-select');
    if (categorySelect) {
      categorySelect.addEventListener('change', (e) => {
        const selectedCategory = e.target.value;
        this.updateCategorySelectionState(selectedCategory);
      });
    }
  }

  // Update category selection state
  updateCategorySelectionState(selectedCategory) {
    const nextBtn = document.querySelector('[data-step="3"] .rai-next-btn');
    if (nextBtn) {
      nextBtn.disabled = !selectedCategory;
    }
  }

  // Initialize model input
  initModelInput() {
    const modelInput = document.getElementById('model-input');
    if (modelInput) {
      modelInput.addEventListener('input', (e) => {
        const modelText = e.target.value.trim();
        this.updateModelInputState(modelText);
      });
    }
  }

  // Update model input state
  updateModelInputState(modelText) {
    const nextBtn = document.querySelector('[data-step="4"] .rai-next-btn');
    if (nextBtn) {
      nextBtn.disabled = !modelText;
    }
  }

  // Initialize condition selection
  initConditionSelection() {
    const conditionSelect = document.getElementById('condition-select');
    if (conditionSelect) {
      conditionSelect.addEventListener('change', (e) => {
        const selectedCondition = e.target.value;
        this.updateConditionSelectionState(selectedCondition);
      });
    }
  }

  // Update condition selection state
  updateConditionSelectionState(selectedCondition) {
    const nextBtn = document.querySelector('[data-step="5"] .rai-next-btn');
    if (nextBtn) {
      nextBtn.disabled = !selectedCondition;
    }
  }

  // Initialize details form
  initDetailsForm() {
    const firstNameInput = document.getElementById('first-name');
    const emailInput = document.getElementById('email-address');
    
    if (firstNameInput && emailInput) {
      // Check both fields on input
      const checkFields = () => {
        const firstName = firstNameInput.value.trim();
        const email = emailInput.value.trim();
        this.updateDetailsFormState(firstName, email);
      };
      
      firstNameInput.addEventListener('input', checkFields);
      emailInput.addEventListener('input', checkFields);
    }
  }

  // Update details form state
  updateDetailsFormState(firstName, email) {
    const nextBtn = document.querySelector('[data-step="8"] .rai-next-btn');
    if (nextBtn) {
      const isValid = firstName && email && this.isValidEmail(email);
      nextBtn.disabled = !isValid;
    }
  }

  // Email validation helper
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Initialize terms agreement
  initTermsAgreement() {
    const termsCheckbox = document.getElementById('terms-agreement');
    if (termsCheckbox) {
      termsCheckbox.addEventListener('change', (e) => {
        const isAgreed = e.target.checked;
        this.updateTermsAgreementState(isAgreed);
      });
    }
  }

  // Update terms agreement state
  updateTermsAgreementState(isAgreed) {
    const nextBtn = document.querySelector('[data-step="9"] .rai-next-btn');
    if (nextBtn) {
      nextBtn.disabled = !isAgreed;
    }
  }

  // Submit quote to API
  async submitQuote() {
    try {
      // Show loading slide (step 10)
      this.setStep(10);
      
      // Transition to "Getting you the best estimate..." after 2 seconds
      setTimeout(() => {
        this.setStep(10.5);
      }, 2000);
      
      // Wait longer to ensure second loading slide is visible before API call
      await new Promise(resolve => setTimeout(resolve, 5000));
      
      // Create FormData for file uploads
      const formData = new FormData();
      
      // Add form fields
      const formFields = this.collectFormData();
      Object.keys(formFields).forEach(key => {
        formData.append(key, formFields[key]);
      });
      
      // Add image files
      const imageTypes = ['front', 'back', 'interior', 'bottom'];
      imageTypes.forEach(type => {
        if (this.photos[type]) {
          formData.append(`${type}_image`, this.photos[type]);
        }
      });
      
      // Send API request - use relative path for local, absolute for production
      const apiUrl = window.location.hostname === 'reluxify.ca' 
        ? 'https://test.reluxify.ca/api/quote'
        : '/api/quote';
      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      // Quote submitted successfully
      
      // Store the result for display in success slide
      this.lastQuoteResult = result;
      
      // Populate the quote results slide with actual data
      this.populateQuoteResults(result);
      
      // Show quote results slide (step 11)
      this.setStep(11);
      
    } catch (error) {
      // Handle quote submission error
      this.showErrorMessage('Failed to submit quote. Please try again.');
      this.setStep(12);
    }
  }

  // Show error message to user
  showErrorMessage(message) {
    const errorSlide = document.getElementById('rai-error-slide');
    if (errorSlide) {
      const errorText = errorSlide.querySelector('.rai-error-message');
      if (errorText) {
        errorText.textContent = message;
      }
    }
  }

  // Populate quote results slide with actual data
  populateQuoteResults(result) {
    // Get AI analysis data
    const aiAnalysis = result.ai_analysis || {};
    
    // Check confidence score - hide price if below 70%
    const confidenceScore = aiAnalysis.confidence_score || 0;
    const confidencePercentage = Math.round(confidenceScore * 100);
    
    // Get price elements
    const minPriceEl = document.getElementById('quote-min-price');
    const maxPriceEl = document.getElementById('quote-max-price');
    const priceRangeEl = document.querySelector('.rai-price-range');
    const priceContainerEl = document.querySelector('.rai-price-container');
    
    // Show/hide price based on confidence
    if (confidencePercentage >= 70) {
      // Show price - confidence is high enough
      if (priceRangeEl) priceRangeEl.style.display = 'block';
      if (priceContainerEl) priceContainerEl.style.display = 'block';
      
      if (minPriceEl) {
        if (aiAnalysis.min_price !== undefined && aiAnalysis.min_price !== null) {
          const roundedMin = Math.round(aiAnalysis.min_price);
          minPriceEl.textContent = roundedMin;
        }
      }
      
      if (maxPriceEl) {
        if (aiAnalysis.max_price !== undefined && aiAnalysis.max_price !== null) {
          const roundedMax = Math.round(aiAnalysis.max_price);
          maxPriceEl.textContent = roundedMax;
        }
      }
    } else {
      // Hide price - confidence is too low
      if (priceRangeEl) priceRangeEl.style.display = 'none';
      if (priceContainerEl) priceContainerEl.style.display = 'none';
    }
    
    // Update quote explanation with AI analysis summary
    const explanationEl = document.getElementById('quote-explanation');
    if (explanationEl) {
      let explanationText = '';
      
      if (aiAnalysis.analysis_summary) {
        // Use the full analysis summary from AI
        explanationText = aiAnalysis.analysis_summary;
      } else {
        explanationText = 'Quote analysis completed.';
      }
      
      // Add confidence warning if price is hidden
      if (confidencePercentage < 70) {
        explanationText += `\n\nNote: We cannot provide a reliable price estimate at this time. Please ensure all images are clear and match the selected category for a more accurate assessment.`;
      }
      
      explanationEl.textContent = explanationText;
    }
    
    // Update expiration time from AI response
    const daysEl = document.getElementById('quote-expires-days');
    const hoursEl = document.getElementById('quote-expires-hours');
    
    if (aiAnalysis.expires_at) {
      // Parse the UTC timestamp properly - ensure it's treated as UTC
      const expiresAt = new Date(aiAnalysis.expires_at + (aiAnalysis.expires_at.includes('Z') ? '' : 'Z'));
      const now = new Date();
      
      // Calculate time difference in milliseconds
      const timeDiff = expiresAt.getTime() - now.getTime();
      const days = Math.max(0, Math.floor(timeDiff / (1000 * 60 * 60 * 24)));
      const hours = Math.max(0, Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
      
      if (daysEl) {
        daysEl.textContent = days;
      }
      if (hoursEl) {
        hoursEl.textContent = hours;
      }
    } else {
      // Fallback: set default expiration (4 days 23 hours)
      if (daysEl) daysEl.textContent = '4';
      if (hoursEl) hoursEl.textContent = '23';
    }
  }

  // Collect all form data
  collectFormData() {
    const formData = {
      name: document.getElementById('first-name')?.value || '',
      email: document.getElementById('email-address')?.value || '',
      brand: document.getElementById('brand-select')?.value || '',
      category: document.getElementById('category-select')?.value || '',
      model_style: document.getElementById('model-input')?.value || '',
      condition: document.getElementById('condition-select')?.value || '',
      additional_notes: document.getElementById('notes-input')?.value || '',
      original_box: document.getElementById('original-box')?.checked || false,
      original_dust_bag: document.getElementById('original-dust-bag')?.checked || false,
      receipt_of_purchase: document.getElementById('receipt-of-purchase')?.checked || false,
      authenticity_certificate: document.getElementById('authenticity-certificate')?.checked || false
    };

    return formData;
  }

  // Get selected inclusions
  getSelectedInclusions() {
    const selectedInclusions = [];
    const inclusionCheckboxes = document.querySelectorAll('input[name="inclusions"]:checked');
    
    inclusionCheckboxes.forEach(checkbox => {
      selectedInclusions.push(checkbox.value);
    });

    return selectedInclusions;
  }

  // Handle successful quote submission (now handled by success slide)
  handleQuoteSubmissionSuccess(result) {
    // Success is now handled by the success slide (step 11)
  }

  // Display AI analysis data in success slide
  displayAIAnalysis() {
    if (!this.lastQuoteResult || !this.lastQuoteResult.ai_analysis) {
      return;
    }

    const aiData = this.lastQuoteResult.ai_analysis;
    const successSlide = document.getElementById('rai-success-slide');
    
    if (!successSlide) return;

    // Create AI analysis display
    let aiHtml = '';
    
    if (aiData.error) {
      // Show error state
      aiHtml = `
        <div class="rai-ai-analysis-error">
          <h4>AI Analysis Temporarily Unavailable</h4>
          <p>${aiData.error}</p>
          <p>Your quote has been submitted and we'll contact you within 24 hours.</p>
        </div>
      `;
    } else {
      // Show successful AI analysis
      const priceRange = `$${aiData.min_price?.toFixed(2) || 'N/A'} - $${aiData.max_price?.toFixed(2) || 'N/A'}`;
      const confidence = Math.round((aiData.confidence_score || 0) * 100);
      
      aiHtml = `
        <div class="rai-ai-analysis-success">
          <h4>🤖 AI Analysis Complete</h4>
          <div class="rai-price-range">
            <strong>Estimated Value:</strong> ${priceRange}
          </div>
          <div class="rai-confidence">
            <strong>Confidence:</strong> ${confidence}%
          </div>
          <div class="rai-analysis-summary">
            <strong>Analysis:</strong> ${aiData.analysis_summary || 'Analysis completed successfully.'}
          </div>
          ${aiData.condition_assessment ? `
            <div class="rai-condition-assessment">
              <strong>Condition:</strong> ${aiData.condition_assessment}
            </div>
          ` : ''}
          ${aiData.vision_reason ? `
            <div class="rai-vision-reason">
              <strong>Photo Assessment:</strong> ${aiData.vision_reason}
            </div>
          ` : ''}
          ${aiData.inclusions_reason ? `
            <div class="rai-inclusions-reason">
              <strong>Inclusions:</strong> ${aiData.inclusions_reason}
            </div>
          ` : ''}
          ${aiData.expires_at ? `
            <div class="rai-expiry-info">
              <small>This analysis expires on ${new Date(aiData.expires_at).toLocaleDateString()}</small>
            </div>
          ` : ''}
        </div>
      `;
    }

    // Update the success slide content
    const content = successSlide.querySelector('.rai-success-container');
    if (content) {
      content.innerHTML = `
        <div class="rai-success-icon">✅</div>
        <h3 class="rai-title mb-4 text-center"><strong>Quote Submitted Successfully!</strong></h3>
        <p class="rai-description text-center mb-6">Thank you for your submission. We'll contact you within 24 hours.</p>
        ${aiHtml}
        <div class="rai-quote-id">
          <strong>Quote ID:</strong> ${this.lastQuoteResult.quote_id || 'N/A'}
        </div>
      `;
    }
  }

  // Handle quote submission error (now handled by error slide)
  handleQuoteSubmissionError(error) {
    // Error is now handled by the error slide (step 12)
  }

  // Initialize photo upload areas
  initPhotoUploads() {
    const photoUploaders = document.querySelectorAll('.rai-photo-uploader');
    
    photoUploaders.forEach(uploader => {
      const photoType = uploader.getAttribute('data-photo');
      
      // Create hidden file input
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/*';
      fileInput.style.display = 'none';
      fileInput.setAttribute('data-photo-type', photoType);
      
      // Add file input to uploader
      uploader.appendChild(fileInput);
      
      // Handle file selection
      fileInput.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (file) {
          this.handlePhotoUpload(photoType, file, uploader);
        }
      });
    });
    
    // Use event delegation for click handling
    document.addEventListener('click', (e) => {
      const uploader = e.target.closest('.rai-photo-uploader');
      if (uploader) {
        const fileInput = uploader.querySelector('input[type="file"]');
        if (fileInput) {
          fileInput.click();
        }
      }
    });
  }

  // Handle photo upload
  handlePhotoUpload(photoType, file, uploader) {
    // Validate file
    if (!this.validatePhotoFile(file)) {
      return;
    }

    // Store photo in state
    this.photos[photoType] = file;
    
    // Update UI to show preview
    this.showPhotoPreview(photoType, file, uploader);
    
    // Check if NEXT button should be enabled
    this.updateNextButtonState();
  }

  // Validate photo file
  validatePhotoFile(file) {
    // Check file type
    if (!file.type.startsWith('image/')) {
      this.showErrorMessage('Please select an image file (JPG/PNG)');
      return false;
    }
    
    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      this.showErrorMessage('File size must be less than 10MB');
      return false;
    }
    
    return true;
  }

  // Show photo preview
  showPhotoPreview(photoType, file, uploader) {
    const reader = new FileReader();
    reader.onload = (e) => {
      // Clear existing content
      uploader.innerHTML = '';
      
      // Create preview container
      const preview = document.createElement('div');
      preview.className = 'rai-photo-preview';
      
      // Create image
      const img = document.createElement('img');
      img.src = e.target.result;
      img.alt = `${photoType} photo`;
      img.className = 'rai-photo-image';
      
      // Create remove button
      const removeBtn = document.createElement('button');
      removeBtn.className = 'rai-photo-remove';
      removeBtn.innerHTML = '×';
      removeBtn.onclick = (e) => {
        e.stopPropagation();
        this.removePhoto(photoType, uploader);
      };
      
      // Add elements to preview
      preview.appendChild(img);
      preview.appendChild(removeBtn);
      
      // Add preview to uploader
      uploader.appendChild(preview);
      
      // Re-add file input for future uploads
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/*';
      fileInput.style.display = 'none';
      fileInput.setAttribute('data-photo-type', photoType);
      fileInput.addEventListener('change', (e) => {
        const newFile = e.target.files[0];
        if (newFile) {
          this.handlePhotoUpload(photoType, newFile, uploader);
        }
      });
      uploader.appendChild(fileInput);
    };
    
    reader.readAsDataURL(file);
  }

  // Remove photo
  removePhoto(photoType, uploader) {
    // Clear from state
    this.photos[photoType] = null;
    
    // Reset UI
    this.resetPhotoUploader(uploader, photoType);
    
    // Check NEXT button state
    this.updateNextButtonState();
  }

  // Reset photo uploader to initial state
  resetPhotoUploader(uploader, photoType) {
    uploader.innerHTML = '';
    
    // Recreate placeholder
    const placeholder = document.createElement('div');
    placeholder.className = 'rai-photo-placeholder';
    
    const label = document.createElement('span');
    label.className = 'rai-photo-label';
    label.textContent = this.getPhotoLabel(photoType);
    
    placeholder.appendChild(label);
    
    // Add optional text for optional photos
    if (photoType === 'serialNum' || photoType === 'damage') {
      const optional = document.createElement('span');
      optional.className = 'rai-photo-optional';
      optional.textContent = '(optional)';
      placeholder.appendChild(optional);
    }
    
    uploader.appendChild(placeholder);
    
    // Re-add file input
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.style.display = 'none';
    fileInput.setAttribute('data-photo-type', photoType);
    fileInput.addEventListener('change', (e) => {
      const file = e.target.files[0];
      if (file) {
        this.handlePhotoUpload(photoType, file, uploader);
      }
    });
    uploader.appendChild(fileInput);
  }

  // Get photo label text
  getPhotoLabel(photoType) {
    const labels = {
      front: 'Front',
      back: 'Back',
      interior: 'Interior',
      bottom: 'Bottom',
      serialNum: 'Serial number',
      damage: 'Wear/damage'
    };
    return labels[photoType] || photoType;
  }

  // Update NEXT button state based on required photos
  updateNextButtonState() {
    const nextBtn = document.querySelector('.rai-next-btn');
    if (nextBtn) {
      const requiredPhotos = ['front', 'back', 'interior', 'bottom'];
      const hasRequiredPhotos = requiredPhotos.every(type => this.photos[type] !== null);
      
      nextBtn.disabled = !hasRequiredPhotos;
    }
  }

  // Equivalent to React's setStep function
  setStep(step) {
    if (step >= 0 && step <= this.totalSteps - 1) {
      this.currentStep = step;
      this.showStep(step);
    } else if (step === 10.5) {
      // Handle transition step 10.5
      this.currentStep = step;
      this.showStep(step);
    }
  }

  nextStep() {
    const nextStep = this.currentStep + 1;
    
    // If we're on step 9 (the last form step), submit the quote
    if (this.currentStep === 9) {
      this.submitQuote();
    } else if (nextStep < this.totalSteps) {
      // Otherwise, go to the next step
      this.setStep(nextStep);
    }
  }

  previousStep() {
    const prevStep = this.currentStep - 1;
    
    // Safety check to prevent going below 0
    if (prevStep >= 0) {
      this.setStep(prevStep);
    }
  }

  showStep(step) {
    // Ensure step is within valid range or is the transition step
    if ((step < 0 || step > this.totalSteps - 1) && step !== 10.5) {
      return;
    }
    
    // Hide all slides first
    const allSlides = document.querySelectorAll('.rai-slide');
    allSlides.forEach(slide => {
      slide.classList.remove('active', 'slide-enter', 'slide-enter-active');
    });

    // Show current slide with smooth animation
    const currentSlide = document.querySelector(`[data-step="${step}"]`);
    if (currentSlide) {
      // Add entrance animation
      currentSlide.classList.add('slide-enter');
      
      // Trigger reflow
      currentSlide.offsetHeight;
      
      // Remove entrance class and add active
      currentSlide.classList.remove('slide-enter');
      currentSlide.classList.add('active');
    }

    // Update progress indicator
    this.updateProgress(step);
    
    // Special handling for success slide (step 11) - display AI analysis
    if (step === 11) {
      // Use setTimeout to ensure the slide is visible before updating content
      setTimeout(() => {
        this.displayAIAnalysis();
      }, 100);
    }
    
    // Update navigation state
    this.updateNavigationState(step);
  }

  // Hide all slides to prevent conflicts
  hideAllSlides() {
    const allSlides = document.querySelectorAll('.rai-slide');
    allSlides.forEach(slide => {
      slide.classList.remove('active', 'slide-enter', 'slide-enter-active');
    });
  }

  updateProgress(step) {
    const progressElements = document.querySelectorAll('.rai-progress');
    progressElements.forEach(element => {
      element.textContent = `${step + 1} of ${this.totalSteps}`;
    });
    
    // Update progress bar width
    this.updateProgressBar(step);
  }

  updateProgressBar(step) {
    // Calculate progress percentage based on total steps
    const progressPercentage = Math.min((step + 1) / this.totalSteps * 100, 100);
    
    // Update all progress bars with smooth animation
    const progressBars = document.querySelectorAll('.rai-progress-bar');
    progressBars.forEach(bar => {
      bar.style.width = `${progressPercentage}%`;
    });
  }

  updateNavigationState(step) {
    // Show/hide back button based on step
    const backBtns = document.querySelectorAll('.rai-back-btn');
    const nextBtns = document.querySelectorAll('.rai-next-btn');
    
    backBtns.forEach((btn, index) => {
      if (step === 0 || step >= 10) {
        // Hide back button on first step and on loading/success/error slides
        btn.style.display = 'none';
      } else {
        btn.style.display = 'flex';
      }
    });
    
    nextBtns.forEach((btn, index) => {
      if (step >= 10) {
        // Hide next button on loading/success/error slides
        btn.style.display = 'none';
      } else {
        btn.style.display = 'flex';
      }
    });
  }

  // Smooth transition helper
  animateTransition(element, enter = true) {
    return new Promise((resolve) => {
      const animationClass = enter ? 'slide-enter' : 'slide-exit';
      const activeClass = enter ? 'slide-enter-active' : 'slide-exit-active';
      
      element.classList.add(animationClass);
      
      // Trigger reflow
      element.offsetHeight;
      
      element.classList.add(activeClass);
      
      setTimeout(() => {
        element.classList.remove(animationClass, activeClass);
        resolve();
      }, enter ? 600 : 400);
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new RAIQuoteForm();
});

// Export for potential use in other scripts
window.RAIQuoteForm = RAIQuoteForm;
